import { useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, MapPin, Phone, Instagram, Navigation, Mail, Star } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import BookingButtons, { WhatsAppIcon } from "@/components/site/BookingButtons";
import { Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SITE, waLink, telLink } from "@/data/site";

export default function Contact() {
  const [name, setName] = useState("");
  const [range, setRange] = useState(undefined);
  const [guests, setGuests] = useState("");
  const [notes, setNotes] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please tell us your name so we can address you.");
      return;
    }
    const inDate = range?.from ? format(range.from, "dd MMM yyyy") : "flexible";
    const outDate = range?.to ? format(range.to, "dd MMM yyyy") : "flexible";
    const msg = `Namaste! I'd like to book a stay at Vruksha Valley Eco Resort.
Name: ${name}
Check-in: ${inDate}
Check-out: ${outDate}
Guests: ${guests || "not specified"}${notes ? `\nNotes: ${notes}` : ""}`;
    toast.success("Opening WhatsApp with your enquiry…");
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <PageShell testId="contact-page">
      <PageHero
        overline="Contact & Booking"
        title={["Reserve", "Your Escape"]}
        subtitle="Send us your dates on WhatsApp or call directly — we'll take care of the rest."
        image={SITE.heroImage}
      />

      <section className="bg-ivory py-14 md:py-20">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-5 md:grid-cols-2 md:px-10">
          {/* Booking form */}
          <Reveal>
            <h2 className="font-display text-4xl font-bold tracking-tight text-forest md:text-5xl">Booking Enquiry</h2>
            <p className="mt-3 text-charcoal/70">Fill in your details and we&apos;ll open a prefilled WhatsApp message for you.</p>

            <form onSubmit={submit} data-testid="booking-form" className="mt-9 space-y-6">
              <div>
                <Label htmlFor="name" className="text-charcoal">Your Name</Label>
                <Input id="name" data-testid="booking-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ananya Rao" className="mt-2 h-11 bg-card" />
              </div>

              <div>
                <Label className="text-charcoal">Dates</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-dates-trigger"
                      className="mt-2 flex h-11 w-full items-center gap-3 rounded-md border border-input bg-card px-3 text-left text-sm text-charcoal/80"
                    >
                      <CalendarIcon className="h-4 w-4 text-brown" />
                      {range?.from ? (
                        range.to ? `${format(range.from, "dd MMM")} – ${format(range.to, "dd MMM yyyy")}` : format(range.from, "dd MMM yyyy")
                      ) : (
                        <span className="text-muted-foreground">Select check-in &amp; check-out</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={{ before: new Date() }} />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-charcoal">Guests</Label>
                <Select value={guests} onValueChange={setGuests}>
                  <SelectTrigger data-testid="booking-guests" className="mt-2 h-11 bg-card">
                    <SelectValue placeholder="Number of guests" />
                  </SelectTrigger>
                  <SelectContent>
                    {["1 Guest", "2 Guests", "3 Guests", "4 Guests", "5+ Guests"].map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes" className="text-charcoal">Notes (optional)</Label>
                <Textarea id="notes" data-testid="booking-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anniversary, dietary needs, pickup…" className="mt-2 bg-card" rows={3} />
              </div>

              <button
                type="submit"
                data-testid="booking-submit"
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-[#1f8a4c] px-8 py-4 font-medium text-white transition-all hover:-translate-y-0.5 hover:bg-[#1a7a42]"
              >
                <WhatsAppIcon className="h-5 w-5" /> Send Enquiry on WhatsApp
              </button>
            </form>
          </Reveal>

          {/* Details + map */}
          <Reveal delay={0.15}>
            <div className="rounded-sm bg-forest p-8 text-ivory md:p-10">
              <h3 className="font-serif text-3xl">Vruksha Valley Eco Resort</h3>
              <ul className="mt-6 space-y-5 text-ivory/85">
                <li className="flex gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" /><span>{SITE.address}<br /><span className="text-ivory/55">{SITE.plusCode}</span></span></li>
                <li><a href={telLink} className="flex items-center gap-3 hover:text-gold"><Phone className="h-5 w-5 text-gold" /> {SITE.phone}</a></li>
                <li><a href={`tel:${SITE.phone2Intl}`} className="flex items-center gap-3 hover:text-gold"><Phone className="h-5 w-5 text-gold" /> {SITE.phone2}</a></li>
                <li><a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-gold"><Mail className="h-5 w-5 text-gold" /> {SITE.email}</a></li>
                <li><a href={SITE.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-gold"><Instagram className="h-5 w-5 text-gold" /> @vrukshavalley</a></li>
                <li><a href={SITE.googleReviews} target="_blank" rel="noopener noreferrer" data-testid="contact-google-reviews" className="flex items-center gap-3 hover:text-gold"><Star className="h-5 w-5 text-gold" /> Read Our Google Reviews</a></li>
                <li><a href={SITE.mapUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-gold"><Navigation className="h-5 w-5 text-gold" /> Get Directions</a></li>
              </ul>
              <div className="mt-8">
                <BookingButtons variant="dark" />
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-sm border border-charcoal/10">
              <iframe
                title="Vruksha Valley Eco Resort location"
                src="https://www.google.com/maps?q=Vruksha%20Valley%20Eco%20Resort%20Kalasa&output=embed"
                className="h-[300px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
